import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Stats from 'frontend_stats';
import FullstoryUtil from 'fullstory_util';
import { format } from 'date-fns';
import { parseUTC } from 'core/utils';

import Voting from 'core/components/Voting';
import { FlexRow } from 'core/styles';

import Answer from './Answer';
import AnswerList from './AnswerList';
import { getActiveVote } from './utils';
import {
	QuestionWrapper,
	QuestionBody,
	AnswerButton,
	AnswerToggle,
	AnswerToggleText,
	Author,
	CloseIcon,
	Divider,
	VotingContainer,
} from './questionsStyles';
import NoAnswers from './NoAnswers';

import loadable from '@loadable/component';
const AdminTools = loadable(() => import('./AdminTools'));

class Question extends PureComponent {
	state = {
		open: false,
	}

	handleVote = (upVote) => {
		this.props.onVote(upVote);
	}

	handleAnswerVote = (answer, upVote) => {
		this.props.onAnswerVote(answer, upVote);
	}

	handleAnswerClick = () => {
		// If there's 1 (shown by default) or less, the CTA is to answer
		if (this.props.answers.length <= 1) {
			return this.handleAnswerBtn();
		}

		// Otherwise, the CTA is to see more:
		this.toggleAnswerList();
	}

	handleAnswerBtn = () => {
		this.props.onAnswer();
	}

	toggleAnswerList = () => {
		const open = !this.state.open;
		this.setState({ open });

		if (open) {
			FullstoryUtil.sendEvent('Open Answers');
			Stats.trackGeneric('questions', {
				step_name: 'open_answers',
				cus1: this.props.id,
			});
		}
	}

	render() {
		const {
			id, body, author, createdAt, helpful, notHelpful,
			answers, activeVote, last, answerUpVotes, answerDownVotes,
			isAdmin, onDelete, customerId
		} = this.props;
		const { open } = this.state;
		const firstAnswer = answers && answers[0];
		const moreAnswers = answers.slice(1);
		const numMoreAnswers = moreAnswers.length;
		const postDate = format(parseUTC(createdAt), 'LLLL yyyy');

		return (
			<QuestionWrapper data-id={id}>
				<FlexRow>
					<VotingContainer>
						<Voting
							upVotes={helpful}
							downVotes={notHelpful}
							onVote={this.handleVote}
							activeVote={activeVote}
						/>
					</VotingContainer>
					<QuestionBody onClick={this.handleAnswerClick}>
						{`Q: ${body}`}
						<Author>{`Asked by ${author}, ${postDate}`}</Author>
					</QuestionBody>
					<AdminTools
						isAdmin={isAdmin}
						questionId={id}
						onDelete={onDelete}
					/>
				</FlexRow>
				{firstAnswer && (
					<Answer
						{...firstAnswer}
						customerId={customerId}
						activeVote={getActiveVote(firstAnswer.id, answerUpVotes, answerDownVotes)}
						onVote={(upVote) => this.handleAnswerVote(firstAnswer, upVote)}
					/>
				)}
				{answers.length === 0 && <NoAnswers />}
				<AnswerList
					answers={moreAnswers}
					open={open}
					onVote={this.handleAnswerVote}
					answerUpVotes={answerUpVotes}
					answerDownVotes={answerDownVotes}
					customerId={customerId}
				/>
				{open &&
					<AnswerButton
						onClick={this.handleAnswerBtn}
						themeType="antistroke"
						size="sm">
						{'Answer this Question'}
					</AnswerButton>
				}
				<AnswerToggle onClick={this.handleAnswerClick}>
					{open && <CloseIcon />}
					<AnswerToggleText>
						{open ? `Close more answers` : numMoreAnswers > 0 ? `+ See more answers (${numMoreAnswers})` : `+ Add an answer`}
					</AnswerToggleText>
				</AnswerToggle>
				{!last && <Divider />}
			</QuestionWrapper>
		);
	}
}

Question.propTypes = {
	id: PropTypes.number.isRequired,
	body: PropTypes.string.isRequired,
	author: PropTypes.string.isRequired,
	createdAt: PropTypes.string.isRequired,
	customerId: PropTypes.number,
	helpful: PropTypes.number,
	notHelpful: PropTypes.number,
	answers: PropTypes.array,
	activeVote: PropTypes.oneOf(["up", "down"]),
	answerUpVotes: PropTypes.array,
	answerDownVotes: PropTypes.array,
	last: PropTypes.bool,
	onAnswer: PropTypes.func.isRequired,
	onVote: PropTypes.func.isRequired,
	onAnswerVote: PropTypes.func.isRequired,
	isAdmin: PropTypes.bool,
	onDelete: PropTypes.func,
};

Question.defaultProps = {
	answers: [],
	answerUpVotes: [],
	answerDownVotes: [],
	helpful: 0,
	notHelpful: 0,
	activeVote: null,
};

export default Question;
